import { Link, useParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';

import { ROUTES } from '#/config/routes';
import { fetchStockReturnDetail } from '#/api/imsMgmt/receipts/stockReturn/fetchDetail';
import { DocumentStatusBadge } from '#/modules/commonComponents/badge/DocumentStatusBadge';
import { Button } from '#/modules/commonComponents/button';
import PageShell from '#/modules/infra/layout/PageShell';
import StockReturnOperateShell from '#/modules/imsMgmt/receipts/stockReturn/components/StockReturnOperateShell';
import { getStockReturnApiErrorMessage } from '#/modules/imsMgmt/receipts/stockReturn/helper/apiError';
import { canEditStockReturnDraft } from '#/modules/imsMgmt/receipts/stockReturn/helper/returnStatus';
import { fetchWarehouseSelectOptions } from '#/modules/imsMgmt/settings/storePlace/helper/warehouseSelectOptions';
import {
  lookupQueryKey,
  stockReturnDetailQueryKey,
} from '#/utils/queryKeys';

const CreateEditStockReturnPage = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);

  const detailQuery = useQuery({
    queryKey: stockReturnDetailQueryKey(id),
    queryFn: () => fetchStockReturnDetail(id),
    enabled: isEdit,
  });

  const warehouseQuery = useQuery({
    queryKey: lookupQueryKey('warehouseSelect'),
    queryFn: fetchWarehouseSelectOptions,
  });

  const detail = detailQuery.data?.data;
  const pageTitle = isEdit ? '編輯退料單' : '新增退料單';
  const headerStatus = isEdit ? (detail?.status || 'DRAFT') : 'DRAFT';
  const isPageLoading = isEdit && detailQuery.isLoading;
  const isDetailNotFound = isEdit && detailQuery.isError;
  const isReadOnlyEdit = isEdit && detail && !canEditStockReturnDraft(detail.status);
  const warehouseOptions = warehouseQuery.data ?? [];

  return (
    <PageShell variant="formTall">
      <div className="layout-shell__body">
        <div className="w-full">
          <div className="create_edit_page__header">
            <h1 className="page_title mb-0! flex flex-wrap items-center gap-2">
              <span className="border-l-5 border-primary-400 pl-2.5">{pageTitle}</span>
              <DocumentStatusBadge status={headerStatus} size="sm" />
            </h1>
            <Button type="button" variant="outline" asChild>
              <Link to={id ? ROUTES.workOrderReturnDetail(id) : ROUTES.workOrderReturn}>
                {id ? '返回詳情' : '返回列表'}
              </Link>
            </Button>
          </div>

          {isPageLoading ? (
            <p className="text-sm text-slate-500">載入中…</p>
          ) : null}

          {isDetailNotFound ? (
            <p className="text-sm text-destructive">
              {getStockReturnApiErrorMessage(detailQuery.error, '找不到退料單')}
            </p>
          ) : null}

          {isReadOnlyEdit ? (
            <div className="space-y-3">
              <p className="flex flex-wrap items-center gap-2 text-sm text-slate-600">
                <span>僅草稿可編輯。目前狀態為</span>
                <DocumentStatusBadge status={detail.status} size="sm" />
                <span>，請至詳情頁操作。</span>
              </p>
              <Button type="button" asChild>
                <Link to={ROUTES.workOrderReturnDetail(id)}>前往詳情</Link>
              </Button>
            </div>
          ) : null}

          {!isPageLoading && !isDetailNotFound && !isReadOnlyEdit ? (
            <div className="sheet create_edit create_edit_form create_edit_form--page-scroll">
              <div className="create_edit_form__body">
                <StockReturnOperateShell
                  warehouseOptions={warehouseOptions}
                  isWarehouseLoading={warehouseQuery.isLoading}
                  stockReturnId={id ?? null}
                  initialDetail={isEdit ? detail : null}
                  successNavigateTo={isEdit
                    ? null
                    : (result) => {
                      const createdId = result?.data?.id;
                      return createdId
                        ? ROUTES.workOrderReturnDetail(createdId)
                        : ROUTES.workOrderReturn;
                    }}
                  onDetailRefetch={() => detailQuery.refetch()}
                  showCancelButton
                  cancelNavigateTo={id ? ROUTES.workOrderReturnDetail(id) : ROUTES.workOrderReturn}
                />
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </PageShell>
  );
};

export default CreateEditStockReturnPage;
